import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, CreditCard, Sparkle, XCircle, ShieldCheck, Gift, Handshake } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function BillingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");

  const load = async () => {
    setPlans((await api.get("/billing/plans")).data);
    setBilling((await api.get("/billing/me")).data);
    api.get("/billing/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const choose = async (key) => {
    setBusy(key);
    try {
      const lookup_key = `${key}_${cycle === "yearly" ? "yearly" : "monthly"}`;
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(""); }
  };

  const isAdmin = user?.role === "admin";

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(""); }
  };

  return (
    <div>
      <PageHeader overline="Subscription" title="Billing & Plans">
        Unlimited contracts, AI support, and data on an encrypted private server. 14-day trial · 30% off your first
        month · pay yearly for ~1.5 months free.
      </PageHeader>

      {billing && (
        <div className="mb-6 bg-card border border-border rounded-md p-5 flex flex-wrap items-center justify-between gap-3" data-testid="billing-status">
          <div>
            <div className="overline text-muted-foreground">Current plan</div>
            <div className="font-head font-bold text-lg">{billing.plan_name}
              <span className="ml-2 text-xs font-normal capitalize text-muted-foreground">· {billing.status}</span>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            Seats: <b className="text-foreground">{billing.seats_used}</b> / {billing.seat_limit >= 100000 ? "∞" : billing.seat_limit}
            {billing.trial_ends_at && billing.status === "trialing" && <span className="ml-3">Trial ends {String(billing.trial_ends_at).slice(0, 10)}</span>}
          </div>
          {isAdmin && billing.status === "active" && (
            <Button variant="outline" data-testid="manage-billing-btn" disabled={busy === "portal"} onClick={openPortal}>
              <CreditCard size={15} className="mr-1.5" /> {busy === "portal" ? "Opening…" : "Manage billing"}
            </Button>
          )}
        </div>
      )}

      <div className="flex justify-center mb-6">
        <Tabs value={cycle} onValueChange={setCycle}>
          <TabsList>
            <TabsTrigger value="monthly" data-testid="cycle-monthly">Monthly</TabsTrigger>
            <TabsTrigger value="yearly" data-testid="cycle-yearly">Yearly · save ~12%</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((p) => {
          const price = cycle === "yearly" ? p.yearly : p.monthly;
          const current = billing?.plan === p.key;
          return (
            <div key={p.key} data-testid={`plan-${p.key}`} className={`rounded-md border p-5 flex flex-col ${current ? "border-gold ring-1 ring-gold" : "border-border"} bg-card`}>
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-gold" />
                <span className="font-head font-bold">{p.name}</span>
                {current && <span className="ml-auto text-[10px] uppercase bg-gold/15 text-foreground px-2 py-0.5 rounded-sm">current</span>}
              </div>
              <div className="mt-3">
                <span className="font-head font-bold text-3xl">${cycle === "yearly" ? Math.round(price) : price}</span>
                <span className="text-sm text-muted-foreground">/{cycle === "yearly" ? "yr" : "mo"}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{p.seats >= 100000 ? "Unlimited seats" : `Up to ${p.seats} seats`}</div>
              <p className="text-sm text-muted-foreground mt-3 flex-1">{p.blurb}</p>
              <Button className="mt-4" data-testid={`choose-${p.key}`} disabled={!isAdmin || busy === p.key} onClick={() => choose(p.key)}>
                <CreditCard size={15} className="mr-1.5" /> {busy === p.key ? "Redirecting…" : current ? "Change plan" : "Choose plan"}
              </Button>
            </div>
          );
        })}
      </div>
      {!isAdmin && <p className="text-xs text-muted-foreground mt-4">Only the organization admin can change the subscription.</p>}

      {isAdmin && invoices.length > 0 && (
        <div className="mt-10">
          <div className="overline text-muted-foreground mb-2">Invoice history</div>
          <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="invoice-history">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium">Invoice</th>
                  <th className="px-4 py-2.5 font-medium">Amount</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium text-right">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-${inv.id}`} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5">{inv.created}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{inv.number || "—"}</td>
                    <td className="px-4 py-2.5 font-medium">${inv.amount.toFixed(2)} {inv.currency}</td>
                    <td className="px-4 py-2.5"><span className={`text-[11px] px-2 py-0.5 rounded-sm border capitalize ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}>{inv.status}</span></td>
                    <td className="px-4 py-2.5 text-right">
                      {inv.invoice_pdf || inv.hosted_invoice_url ? (
                        <a href={inv.invoice_pdf || inv.hosted_invoice_url} target="_blank" rel="noreferrer" data-testid={`invoice-view-${inv.id}`} className="text-primary hover:underline">View</a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2 mt-8">
        <AddonCard isAdmin={isAdmin} onPortal={openPortal} />
        <LoyaltyOffer isAdmin={isAdmin} />
      </div>
      <ConsultationCard orgName={user?.org_name} defaultEmail={user?.email} defaultName={user?.name} />
    </div>
  );
}

function AddonCard({ isAdmin, onPortal }) {
  const [info, setInfo] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing/addons").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!info) return null;
  const addon = info.addons[0];
  const active = info.audit_addon;
  const basic = info.included_basic;
  const price = cycle === "yearly" ? addon.yearly : addon.monthly;

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/addon/checkout", { addon: "audit_addon", cycle, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-5 flex flex-col" data-testid="addon-card">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} weight="fill" className="text-gold" />
        <span className="font-head font-bold">Audit &amp; Compliance</span>
        {active && <span className="ml-auto text-[10px] uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm" data-testid="addon-active-badge">active</span>}
        {!active && basic && <span className="ml-auto text-[10px] uppercase bg-gold/15 text-foreground px-2 py-0.5 rounded-sm" data-testid="addon-basic-badge">basic included</span>}
      </div>
      <p className="text-sm text-muted-foreground mt-3 flex-1">{addon.blurb}</p>
      {active ? (
        <>
          <div className="text-sm text-emerald-700 mt-4 flex items-center gap-1.5"><CheckCircle size={16} weight="fill" /> Included in your subscription</div>
          {isAdmin && <Button variant="outline" className="mt-3" data-testid="addon-manage-btn" onClick={onPortal}><CreditCard size={15} className="mr-1.5" /> Manage / cancel</Button>}
        </>
      ) : (
        <>
          {basic && <div className="text-xs text-primary font-medium mt-3">Enterprise price — 25% of standard</div>}
          <div className="mt-2 flex items-center gap-3">
            <div><span className="font-head font-bold text-2xl">${price}</span><span className="text-sm text-muted-foreground">/{cycle === "yearly" ? "yr" : "mo"}</span></div>
            <Tabs value={cycle} onValueChange={setCycle}>
              <TabsList className="h-8">
                <TabsTrigger value="monthly" className="text-xs h-6" data-testid="addon-cycle-monthly">Monthly</TabsTrigger>
                <TabsTrigger value="yearly" className="text-xs h-6" data-testid="addon-cycle-yearly">Yearly</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Button className="mt-4" data-testid="addon-buy-btn" disabled={!isAdmin || busy} onClick={buy}>
            <CreditCard size={15} className="mr-1.5" /> {busy ? "Redirecting…" : basic ? "Upgrade to full" : "Add to plan"}
          </Button>
          {!isAdmin && <p className="text-[11px] text-muted-foreground mt-2">Only the admin can add this.</p>}
        </>
      )}
    </div>
  );
}

function LoyaltyOffer({ isAdmin }) {
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing/retention-offer").then((r) => setOffer(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!offer || (!offer.eligible && !offer.claimed)) return null;

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/retention-offer/claim");
      toast.success(`Loyalty discount applied — ${data.percent_off}% off your next renewal`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-gold ring-1 ring-gold/40 bg-gradient-to-br from-gold/10 to-card p-5 flex flex-col" data-testid="loyalty-offer">
      <div className="flex items-center gap-2">
        <Gift size={18} weight="fill" className="text-gold" />
        <span className="font-head font-bold">Loyalty reward</span>
      </div>
      {offer.claimed ? (
        <p className="text-sm text-muted-foreground mt-3 flex-1 flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-emerald-600" /> Your {offer.percent_off}% continuance discount has been applied. Thank you for {Math.floor(offer.days_as_customer / 30)} months with grantSME!</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mt-3 flex-1">
            You've trusted grantSME for over a year ({offer.days_as_customer} days). As a thank-you, claim a one-time
            <b className="text-foreground"> {offer.percent_off}% continuance discount</b> on your next renewal.
          </p>
          <Button className="mt-4 bg-gold text-primary hover:bg-gold/90" data-testid="loyalty-claim-btn" disabled={!isAdmin || busy} onClick={claim}>
            <Gift size={15} className="mr-1.5" /> {busy ? "Applying…" : `Claim ${offer.percent_off}% off`}
          </Button>
          {!isAdmin && <p className="text-[11px] text-muted-foreground mt-2">Only the admin can claim this.</p>}
        </>
      )}
    </div>
  );
}

function ConsultationCard({ orgName, defaultEmail, defaultName }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: defaultName || "", email: defaultEmail || "", phone: "", org: orgName || "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    setBusy(true);
    try {
      await api.post("/billing/consultation", form);
      toast.success("Request received — our team will reach out to schedule your consultation");
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-primary text-white p-6 mt-4 flex flex-wrap items-center justify-between gap-5" data-testid="consultation-card">
      <div className="flex items-start gap-4 max-w-2xl">
        <div className="h-11 w-11 rounded-md bg-gold/25 flex items-center justify-center shrink-0"><Handshake size={24} weight="fill" className="text-gold" /></div>
        <div>
          <div className="font-head font-bold text-lg">Build Your Own Knowledge Base &amp; Audit</div>
          <p className="text-sm text-white/75 mt-1">
            A one-time setup consultation plus quarterly reviews with a dedicated grantSME specialist (10+ years in
            government procedures). 1–2 hours per session to tailor your knowledge base, audit preferences and
            procedures. Pricing is discussed at your initial consultation.
          </p>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="bg-gold text-primary hover:bg-gold/90 shrink-0" data-testid="consultation-request-btn">Request a consultation</Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Request a consultation</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">Tell us a little about your needs and our team will reach out to schedule and discuss pricing.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="consult-name" value={form.name} onChange={set("name")} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="consult-email" value={form.email} onChange={set("email")} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="consult-phone" value={form.phone} onChange={set("phone")} placeholder="Optional" /></div>
            <div className="space-y-1.5"><Label>Organization</Label><Input data-testid="consult-org" value={form.org} onChange={set("org")} /></div>
            <div className="col-span-2 space-y-1.5"><Label>What would you like to cover?</Label><Textarea data-testid="consult-notes" value={form.notes} onChange={set("notes")} rows={3} placeholder="Knowledge base topics, audit procedures, compliance goals…" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="consult-submit" onClick={submit} disabled={busy}>{busy ? "Sending…" : "Send request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BillingSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch {}
      if (++tries < 8) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
  }, [location.search]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-sm">
        {state === "paid" ? <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto" /> : <CreditCard size={56} className="text-primary mx-auto" />}
        <h2 className="font-head font-bold text-xl mt-4">
          {state === "checking" && "Confirming your subscription…"}
          {state === "paid" && "You're all set!"}
          {state === "timeout" && "Payment received — finalizing"}
          {state === "error" && "Missing session"}
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          {state === "paid" ? "Your plan is active and seats are unlocked." : "This can take a few seconds."}
        </p>
        <Button className="mt-5" data-testid="billing-success-continue" onClick={() => navigate("/billing")}>Back to Billing</Button>
      </div>
    </div>
  );
}

export function BillingCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <XCircle size={56} className="text-muted-foreground mx-auto" />
        <h2 className="font-head font-bold text-xl mt-4">Checkout canceled</h2>
        <p className="text-sm text-muted-foreground mt-2">No charge was made.</p>
        <Button className="mt-5" data-testid="billing-cancel-back" onClick={() => navigate("/billing")}>Back to Billing</Button>
      </div>
    </div>
  );
}
